import { TPaginate } from '@@types/base';
import {
  QueryFunction,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

export type TUsePaginQueryProps<T> = {
  queryKey: QueryKey;
  queryFn: QueryFunction<TPaginate<T>, QueryKey>;
  options?:
    | Omit<
        UseInfiniteQueryOptions<TPaginate<T>, AxiosError<unknown, any>>,
        'queryKey' | 'queryFn'
      >
    | undefined;
};

export default function usePagingQuery<T>({
  queryKey,
  queryFn,
  options,
}: TUsePaginQueryProps<T> & { pageTakeLocalStorageKey?: string }) {
  const router = useRouter();
  const query = useInfiniteQuery<TPaginate<T>, AxiosError<unknown, any>>(
    queryKey,
    async (context) => {
      context.pageParam = {
        page: router.query.page || 1,
        take: process.env.NEXT_PUBLIC_PAGE_DEFAULT_TAKE,
        ...context.pageParam,
      };

      const response = await queryFn(context);
      return response;
    },
    {
      ...options,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return false;
        return lastPage.currentPage < lastPage.totalPage;
      },
    },
  );

  useEffect(() => {
    return () => {
      query.remove();
    };
  }, []);

  const currentData =
    query.data?.pages && query.data?.pages[query.data.pages.length - 1];

  return {
    ...query,
    currentData,
    currentPage: currentData?.currentPage,
  };
}
